"use client";
import React, { useState, useRef, useEffect } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import { useNavItems } from "@/lib/navItems";
import { useNavItemsCorp } from "@/lib/navItemsCorp";
import NavMenu from "./navMenu";
import NavPanel from "./navPanel";
import TopBar from "./topBar";
import SkytraxBanner, { SkytraxDrawerLink } from "./skytraxBanner";
import { PORTAL_LINKS } from "@/lib/portalLinks";
import { isRegionalPath, stripLocale } from "@/i18n/config";
import MobileToggle from "./mobileToggle";
import Image from "next/image";
import MobileMenu from "./mobileMenu";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import dynamic from "next/dynamic";
import { X } from "lucide-react";

const HeaderWeather = dynamic(() => import("./HeaderWeather"), {
  ssr: false,
  loading: () => <HeaderWeatherSkeleton />,
});

function HeaderWeatherSkeleton() {
  return (
    <div className="text-[var(--blanco)] text-sm flex items-center gap-3">
      <div className="w-6 h-6 rounded-full bg-white/20 animate-pulse" />
      <div className="hidden xl:block w-24 h-3 bg-white/20 rounded animate-pulse" />
    </div>
  );
}

export default function Header() {
  const pathname = usePathname();
  const t = useTranslations("menuMobile");
  const tPortal = useTranslations("topbar");
  const locale = pathname.split("/")[1];
  const isCorporate = pathname.startsWith(`/${locale}/corporativo`);
  const navItems = useNavItems();
  const navItemsCorp = useNavItemsCorp();
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [hasHero, setHasHero] = useState(false);
  const navItemsToUse = isCorporate ? navItemsCorp : navItems;
  const linkLogo = isCorporate ? `/${locale}/corporativo` : `/${locale}`;

  // Mismo criterio que el topbar: los portales regionales viven en rutas
  // propias (/mppa, /mpda…), no bajo /aeropuertos, así que ese enlace no
  // puede resolverse sólo por prefijo.
  const path = stripLocale(pathname);
  const isPortalActive = (key: string, href: string) => {
    if (key === "regional") return isRegionalPath(path) || path.startsWith(href);
    return href === "/" ? path === "/" : path.startsWith(href);
  };

  const panelRef = useRef<HTMLDivElement>(null);
  const anchorRefs = useRef<(HTMLElement | null)[]>([]);

  // Sólo las páginas con hero a pantalla completa pueden permitirse un header
  // translúcido; en el resto el texto blanco caería sobre fondo claro.
  useEffect(() => {
    setHasHero(Boolean(document.querySelector("[data-hero]")));
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insidePanel = panelRef.current?.contains(target);
      const insideAnchor = anchorRefs.current.some((ref) =>
        ref?.contains(target),
      );
      if (!insidePanel && !insideAnchor) setActiveDropdown(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const toggleNavbar = () => {
    setIsOpen(!isOpen);
    setActiveDropdown(null);
  };

  // Con un panel abierto el header debe ser sólido aunque no se haya bajado:
  // el desplegable necesita un borde del que colgar.
  const isTransparent =
    hasHero && !isScrolled && activeDropdown === null && !isOpen;

  return (
    <>
      {activeDropdown !== null && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[60] transition-all duration-300"
          onClick={() => setActiveDropdown(null)}
        />
      )}

      <header
        className={clsx(
          "fixed z-[70] w-full transition-all duration-300",
          isTransparent
            ? // Nada de `filter` aquí: un filtro en un ancestro convierte al
              // header en bloque contenedor de sus descendientes `fixed`. El
              // hero rota entre fotos muy claras, así que la legibilidad la
              // resuelve un velo denso, no una sombra sobre el texto.
              "bg-[linear-gradient(to_bottom,rgba(12,29,53,0.95)_0%,rgba(16,37,66,0.82)_55%,rgba(16,37,66,0.45)_85%,transparent_100%)]"
            : "bg-[var(--fondoGeneral-secciones)] shadow-lg border-b-2 border-[var(--acento-nav)]",
        )}
      >
        <SkytraxBanner />

        <TopBar isScrolled={isScrolled} />

        {/* Nav de escritorio */}
        <nav
          className={clsx(
            "hidden lg:flex w-[92%] max-w-7xl mx-auto items-center transition-all duration-300",
            isScrolled ? "py-3" : "py-4",
          )}
        >
          <Link
            href={linkLogo}
            // shrink-0: sin esto flexbox comprimía el logo hasta solaparlo
            // con el primer item del menú entre 1024 y 1200 px.
            className="shrink-0 flex items-center gap-2 transition-transform duration-200 hover:scale-105"
          >
            <Image
              src="/Iso_Blanco.svg"
              alt="logo tocumen"
              width={100}
              height={100}
              className={clsx(
                "w-auto object-contain transition-all duration-300",
                isScrolled ? "h-8" : "h-10",
              )}
              priority
              unoptimized
            />
            <span className="text-[var(--blanco)] uppercase text-sm font-bold border-l border-[var(--blanco)] pl-2">
              Tocumen
            </span>
          </Link>

          <div className="flex-1 flex justify-center px-6">
            <NavMenu
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
              anchorRefs={anchorRefs}
              navItems={navItemsToUse}
            />
          </div>

          {/* Con las redes y el idioma en el topbar, aquí sólo queda el clima */}
          <div className="shrink-0 text-[var(--blanco)] flex items-center">
            <HeaderWeather variant="desktop" />
          </div>
        </nav>

        <NavPanel
          item={activeDropdown !== null ? navItemsToUse[activeDropdown] : null}
          onClose={() => setActiveDropdown(null)}
          panelRef={panelRef}
        />

        {/* Nav de teléfono y tablet */}
        <nav
          className={clsx(
            "lg:hidden flex items-center justify-between w-[92%] mx-auto transition-all duration-300",
            isScrolled ? "py-2.5" : "py-4",
          )}
        >
          <Link
            href={linkLogo}
            className="shrink-0 flex items-center gap-2 transition-transform duration-200 active:scale-95"
          >
            <Image
              src="/Iso_Blanco.svg"
              alt="logo tocumen"
              width={80}
              height={80}
              className="h-8 w-auto object-contain"
              priority
              unoptimized
            />
            <span className="text-[var(--blanco)] uppercase text-xs font-bold border-l border-[var(--blanco)] pl-2">
              Tocumen
            </span>
          </Link>

          <div className="flex items-center gap-4">
            <HeaderWeather variant="mobile" />
            <MobileToggle isOpen={isOpen} toggleNavbar={toggleNavbar} />
          </div>
        </nav>
      </header>

      {/* Drawer de teléfono y tablet */}
      {isOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-[80] animate-fade-in"
            onClick={toggleNavbar}
          />

          <div className="lg:hidden fixed top-0 right-0 h-full w-[92%] max-w-lg bg-[var(--fondoGeneral-secciones)] z-[90] shadow-2xl animate-slide-in-right flex flex-col">
            <div className="shrink-0 flex items-center justify-between px-6 py-5 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Image
                  src="/Iso_Blanco.svg"
                  alt="logo tocumen"
                  width={60}
                  height={60}
                  className="h-7 w-auto object-contain"
                  unoptimized
                />
                <span className="text-[var(--blanco)] uppercase text-xs font-bold border-l border-white/50 pl-2">
                  Tocumen
                </span>
              </div>
              <button
                onClick={toggleNavbar}
                aria-label={t("close")}
                className="text-white hover:bg-white/10 p-2 rounded-lg transition-colors cursor-pointer"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* En tablet las secciones caben en dos columnas */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <MobileMenu navItems={navItemsToUse} setIsOpen={setIsOpen} />

              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-xs uppercase tracking-[0.2em] text-white/50 font-semibold mb-3">
                  {t("language")}
                </p>
                <LanguageSwitcher variant="mobile" />
              </div>

              {/* El topbar no existe por debajo de lg: sus portales entran aquí */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-xs uppercase tracking-[0.2em] text-white/50 font-semibold mb-3">
                  {t("portals")}
                </p>
                <ul className="grid grid-cols-2 gap-2">
                  {PORTAL_LINKS.map((portal) => {
                    const active = isPortalActive(portal.key, portal.href);
                    return (
                      <li key={portal.key}>
                        <Link
                          href={portal.href}
                          onClick={() => setIsOpen(false)}
                          aria-current={active ? "page" : undefined}
                          className={clsx(
                            "block px-4 py-3 rounded-xl border text-sm font-medium transition-colors duration-200",
                            active
                              ? "bg-[var(--acento-nav)] border-transparent text-[var(--fondoGeneral-secciones)]"
                              : "bg-white/5 border-white/15 text-white/80 hover:text-[var(--acento-nav)] hover:bg-white/10",
                          )}
                        >
                          {tPortal(portal.key)}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <SkytraxDrawerLink onNavigate={() => setIsOpen(false)} />
            </div>

            <div className="shrink-0 px-6 py-5 border-t border-white/10 flex justify-center">
              {/* Mismo eslogan del footer: el SVG del proyecto es transparente,
                  teñido de blanco para leerse sobre el panel oscuro. */}
              <Image
                src="/Slogan_Azul.svg"
                alt="La Puerta de Las Américas"
                width={711}
                height={167}
                unoptimized
                className="h-9 w-auto object-contain opacity-80 [filter:brightness(0)_invert(1)]"
              />
            </div>
          </div>
        </>
      )}
    </>
  );
}
